// Opening move: SectionTaggingPlugin - handles section wrapping/unwrapping commands
// This plugin enables manual tagging via toolbar controls and keyboard shortcuts
// Big play: Wraps selected paragraphs in SectionBlockNode while preserving cursor position

import { useEffect } from 'react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_EDITOR,
  createCommand,
  type LexicalCommand,
  type RangeSelection,
} from 'lexical'
import {
  $createSectionBlockNode,
  $isSectionBlockNode,
  type SectionBlockNode,
  type SectionType,
} from '../nodes/SectionBlockNode'

// Main play: Define commands for section tagging operations
export const APPLY_SECTION_TAG_COMMAND: LexicalCommand<SectionType> = createCommand('APPLY_SECTION_TAG')
export const CLEAR_SECTION_TAG_COMMAND: LexicalCommand<void> = createCommand('CLEAR_SECTION_TAG')

/**
 * SectionTaggingPlugin - Handles section tagging operations
 *
 * Main play: Registers command listeners for applying and clearing section tags
 * Wraps selected paragraphs in SectionBlockNode or unwraps them back to plain paragraphs
 *
 * Features:
 * - APPLY_SECTION_TAG_COMMAND: Wraps selection in SectionBlockNode
 * - CLEAR_SECTION_TAG_COMMAND: Unwraps SectionBlockNode back to paragraphs
 * - Preserves cursor position during transformations
 * - Handles multi-paragraph selections
 * - Integrates with undo/redo via editor.update
 *
 * Big play: This is the core logic for manual section tagging via toolbar or hotkeys
 */
export default function SectionTaggingPlugin(): null {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    // Main play: Register command listener for applying section tags
    const unregisterApply = editor.registerCommand(
      APPLY_SECTION_TAG_COMMAND,
      (sectionType: SectionType) => {
        editor.update(() => {
          const selection = $getSelection()
          if (!$isRangeSelection(selection)) return

          // Time to tackle the tricky bit: Wrap selected paragraphs in SectionBlockNode
          $wrapSelectionInSection(selection, sectionType)
        }, { tag: 'section-tagging-apply' })
        return true
      },
      COMMAND_PRIORITY_EDITOR
    )

    // Main play: Register command listener for clearing section tags
    const unregisterClear = editor.registerCommand(
      CLEAR_SECTION_TAG_COMMAND,
      () => {
        editor.update(() => {
          const selection = $getSelection()
          if (!$isRangeSelection(selection)) return

          // Time to tackle the tricky bit: Unwrap SectionBlockNode back to paragraphs
          $unwrapSelectionFromSection(selection)
        }, { tag: 'section-tagging-clear' })
        return true
      },
      COMMAND_PRIORITY_EDITOR
    )

    // Victory lap: Clean up command listeners on unmount
    return () => {
      unregisterApply()
      unregisterClear()
    }
  }, [editor])

  return null
}

/**
 * Wrap selected nodes in a SectionBlockNode
 *
 * Time to tackle the tricky bit: Find all selected paragraphs and wrap them
 * in a single SectionBlockNode, preserving text content and cursor position
 */
function $wrapSelectionInSection(selection: RangeSelection, sectionType: SectionType): void {
  // Opening move: Get selected nodes
  const nodes = selection.getNodes()
  if (nodes.length === 0) return

  // Main play: Find all paragraph nodes in selection
  const paragraphs = nodes.filter(node => {
    // Check if node is a paragraph or if its parent is a paragraph
    const parent = node.getParent()
    return node.getType() === 'paragraph' || parent?.getType() === 'paragraph'
  })

  if (paragraphs.length === 0) return

  // Time to tackle the tricky bit: Get the first and last paragraph
  // We need to find the actual paragraph nodes, not text nodes
  const firstParagraph = paragraphs[0].getType() === 'paragraph'
    ? paragraphs[0]
    : paragraphs[0].getParent()
  const lastParagraph = paragraphs[paragraphs.length - 1].getType() === 'paragraph'
    ? paragraphs[paragraphs.length - 1]
    : paragraphs[paragraphs.length - 1].getParent()

  if (!firstParagraph || !lastParagraph) return

  // Big play: Check if paragraphs are already wrapped in a SectionBlockNode
  const parent = firstParagraph.getParent()
  if ($isSectionBlockNode(parent)) {
    // Time to tackle the tricky bit: Update BOTH sectionType AND label to keep badge in sync
    // This ensures the visual badge updates immediately when retagging
    const writable = parent.getWritable()
    writable.setSectionType(sectionType)
    writable.setLabel(undefined) // Clear custom label, use sectionType for badge
    return
  }

  // Main play: Create new SectionBlockNode
  const sectionBlock = $createSectionBlockNode(sectionType)

  // Time to tackle the tricky bit: Collect all siblings between first and last paragraph
  const nodesToWrap = []
  let current: typeof firstParagraph | null = firstParagraph
  while (current && current !== lastParagraph) {
    nodesToWrap.push(current)
    current = current.getNextSibling()
  }
  if (lastParagraph) {
    nodesToWrap.push(lastParagraph)
  }

  // Big play: Insert section block before first paragraph
  firstParagraph.insertBefore(sectionBlock)

  // Main play: Move all paragraphs into section block
  for (const node of nodesToWrap) {
    sectionBlock.append(node)
  }

  // Victory lap: Selection is preserved automatically by Lexical
}

/**
 * Unwrap selected nodes from SectionBlockNode
 *
 * Time to tackle the tricky bit: Find SectionBlockNode ancestors and unwrap
 * their children back to the parent level, preserving text and cursor
 */
function $unwrapSelectionFromSection(selection: RangeSelection): void {
  // Opening move: Get selected nodes
  const nodes = selection.getNodes()
  if (nodes.length === 0) return

  // Main play: Find all SectionBlockNode ancestors
  const sectionsToUnwrap = new Set<SectionBlockNode>()

  for (const node of nodes) {
    // Time to tackle the tricky bit: Walk up the tree to find SectionBlockNode
    let current = node
    while (current) {
      if ($isSectionBlockNode(current)) {
        sectionsToUnwrap.add(current)
        break
      }
      const parent = current.getParent()
      if (!parent) break
      current = parent
    }
  }

  // Big play: Unwrap each section block
  for (const section of sectionsToUnwrap) {
    const children = section.getChildren()
    const parent = section.getParent()
    if (!parent) continue

    // Main play: Move children out of section block
    for (const child of children) {
      section.insertBefore(child)
    }

    // Victory lap: Remove empty section block
    section.remove()
  }

  // Victory lap: Selection is preserved automatically by Lexical
}

// Victory lap: Clean plugin that handles section tagging commands with proper cursor preservation
// All mutations happen inside editor.update for undo/redo integration
